/**
 * AnswerButton Component - Memoized answer button for game screen
 * Optimized to prevent unnecessary re-renders
 */

import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import textToSpeech from '../utils/textToSpeech';
import { HAPTIC_TYPES } from '../constants/audio';

const AnswerButton = memo(({ 
  option, 
  index, 
  currentQuestion,
  answerLocked,
  correctAnswer,
  question,
  questions,
  onAnswer,
  triggerHaptic
}) => {
  const buttonColors = [
    'from-blue-400 via-blue-500 to-blue-600',
    'from-pink-400 via-pink-500 to-pink-600',
    'from-green-400 via-green-500 to-green-600',
    'from-purple-400 via-purple-500 to-purple-600'
  ];
  const textColors = [
    'text-blue-50',
    'text-pink-50',
    'text-green-50',
    'text-purple-50'
  ];
  const colorIndex = index % 4;

  const handleClick = useCallback(() => {
    if (answerLocked) return;
    triggerHaptic(HAPTIC_TYPES.MEDIUM);
    onAnswer(option, correctAnswer, questions, question.explanation);
  }, [answerLocked, option, correctAnswer, questions, question, onAnswer, triggerHaptic]);

  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !answerLocked) {
      e.preventDefault();
      triggerHaptic(HAPTIC_TYPES.MEDIUM);
      onAnswer(option, correctAnswer, questions, question.explanation);
    }
  }, [answerLocked, option, correctAnswer, questions, question, onAnswer, triggerHaptic]);

  const handleSpeak = useCallback(() => {
    textToSpeech.toggleSpeak(option, { rate: 0.92, pitch: 1.05 });
  }, [option]);

  return (
    <div className="flex items-center gap-2 md:gap-3 min-w-0">
      <div 
        onClick={handleClick}
        className={`answer-option flex-1 min-w-0 p-5 md:p-6 text-2xl md:text-3xl font-bold rounded-2xl shadow-xl hover:scale-110 active:scale-95 cursor-pointer bg-gradient-to-br ${buttonColors[colorIndex]} ${textColors[colorIndex]} transition-all duration-200 border-4 border-white min-h-[80px] md:min-h-[100px] flex items-center justify-center whitespace-nowrap`}
        style={{
          boxShadow: '0 8px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
          touchAction: 'manipulation'
        }}
        role="button"
        aria-label={`Answer option: ${option}`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <span className="text-center px-2" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>{option}</span>
      </div>
      <button
        onClick={handleSpeak}
        className="bg-purple-500 hover:bg-purple-600 text-white p-3 md:p-4 rounded-full transition-all shadow-lg flex-shrink-0 min-w-[48px] min-h-[48px] md:min-w-[56px] md:min-h-[56px] text-xl md:text-2xl flex items-center justify-center border-2 border-white"
        style={{ touchAction: 'manipulation' }}
        title="Listen to answer"
        aria-label={`Listen to answer: ${option}`}
      >
        🔊
      </button>
    </div>
  );
});

AnswerButton.propTypes = {
  option: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  currentQuestion: PropTypes.number.isRequired,
  answerLocked: PropTypes.bool.isRequired,
  correctAnswer: PropTypes.string.isRequired,
  question: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  onAnswer: PropTypes.func.isRequired,
  triggerHaptic: PropTypes.func.isRequired
};

AnswerButton.displayName = 'AnswerButton';

export default AnswerButton;

